import { createContext, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";

// Product prices in the DB are stored in GBP. This context exposes the
// currently-selected display currency (persisted per-browser) plus a
// helper to convert + format GBP amounts. Payment always processes in GBP.

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = "pd_currency";

const CURRENCIES = [
  { code: "GBP", symbol: "£", flag: "🇬🇧", label: "GBP" },
  { code: "EUR", symbol: "€", flag: "🇪🇺", label: "EUR" },
  { code: "USD", symbol: "$", flag: "🇺🇸", label: "USD" },
];

// Country ISO → supported display currency. Anything else falls back to GBP.
// Eurozone members map to EUR; UK-linked (GB, IM, JE, GG) stays GBP;
// USD-dominant/proxying regions (US, Latin America commonly pricing in USD) → USD.
const EUROZONE = new Set([
  "AT", "BE", "CY", "DE", "EE", "ES", "FI", "FR", "GR", "HR", "IE", "IT",
  "LT", "LU", "LV", "MT", "NL", "PT", "SI", "SK",
]);
const USD_REGIONS = new Set([
  "US", "PR", "EC", "SV", "PA", "TL", "ZW",
]);

function detectCurrencyFromBrowser() {
  if (typeof window === "undefined" || typeof navigator === "undefined") return "GBP";
  try {
    // 1) Prefer explicit country in navigator.language (e.g. "en-GB", "fr-FR").
    const langs = [navigator.language, ...(navigator.languages || [])].filter(Boolean);
    for (const l of langs) {
      const country = (l.split("-")[1] || "").toUpperCase();
      if (!country) continue;
      if (country === "GB" || country === "IM" || country === "JE" || country === "GG") return "GBP";
      if (EUROZONE.has(country)) return "EUR";
      if (USD_REGIONS.has(country)) return "USD";
    }
    // 2) Fall back to the IANA timezone (broadly reliable).
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    if (tz.startsWith("Europe/London") || tz.startsWith("Europe/Belfast")) return "GBP";
    if (tz.startsWith("Europe/")) return "EUR";
    if (tz.startsWith("America/") || tz === "Pacific/Honolulu" || tz === "Pacific/Guam") return "USD";
  } catch (err) {
    // Non-fatal — currency detection is a UX nicety. Log so we can spot
    // repeated failures in production telemetry rather than swallowing.
    console.warn("Currency auto-detect failed:", err);
  }
  return "GBP";
}

const CurrencyContext = createContext(null);

export function CurrencyProvider({ children }) {
  const [currency, setCurrencyState] = useState(() => {
    if (typeof window === "undefined") return "GBP";
    // sessionStorage (not localStorage) — currency preference is a
    // non-sensitive UX signal, and keeping it session-scoped means it
    // dies with the tab and doesn't accumulate persistent surface for
    // any script running on the page.
    const saved = window.sessionStorage.getItem(STORAGE_KEY);
    if (saved && CURRENCIES.some((c) => c.code === saved)) return saved;
    // No explicit choice yet — auto-detect from the browser once.
    return detectCurrencyFromBrowser();
  });
  // Rates keyed by currency code. Includes GBP: 1 by construction.
  const [rates, setRates] = useState({ GBP: 1, EUR: 1.17, USD: 1.27 });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    // Fetch once on mount. Backend caches, so navigations don't refetch.
    axios
      .get(`${BACKEND}/api/fx/rates`)
      .then((r) => {
        if (r.data?.rates) setRates((prev) => ({ ...prev, ...r.data.rates }));
      })
      .catch((err) => {
        // Keep fallback rates; log so a persistent FX outage surfaces.
        console.warn("FX rates fetch failed, using fallback:", err);
      })
      .finally(() => setLoaded(true));
  }, []);

  const setCurrency = (code) => {
    if (!CURRENCIES.some((c) => c.code === code)) return;
    setCurrencyState(code);
    try {
      window.sessionStorage.setItem(STORAGE_KEY, code);
    } catch (err) {
      console.warn("Failed to persist currency preference:", err);
    }
  };

  const value = useMemo(() => {
    const meta = CURRENCIES.find((c) => c.code === currency) || CURRENCIES[0];
    const rate = rates[currency] || 1;
    const convert = (gbp) => {
      const n = Number(gbp) || 0;
      return n * rate;
    };
    const format = (gbp, { fractionDigits } = {}) => {
      const converted = convert(gbp);
      // Always render two decimal places by default, even when the amount
      // happens to be a whole number (£50 → £50.00). Prices sitewide are
      // stored to 2 dp in GBP and buyers expect that precision — a stray
      // "£50" next to a "£49.99" reads like a rounding bug. Callers who
      // truly want a different precision (rare) can still pass an
      // explicit `fractionDigits`.
      const digits = typeof fractionDigits === "number" ? fractionDigits : 2;
      return `${meta.symbol}${converted.toFixed(digits)}`;
    };
    return {
      currency,
      currencies: CURRENCIES,
      symbol: meta.symbol,
      flag: meta.flag,
      label: meta.label,
      rate,
      rates,
      loaded,
      setCurrency,
      convert,
      format,
      isGBP: currency === "GBP",
    };
  }, [currency, rates, loaded]);

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error("useCurrency must be used within CurrencyProvider");
  return ctx;
}
